import { useEffect, useState, useCallback } from "react";
import { CaretUp, CaretDown, CaretLeft, CaretRight, MagnifyingGlass } from "@phosphor-icons/react";
import api from "../lib/api";
import { Input } from "./ui/input";

const PAGE_SIZE = 15;

export default function DataTablePanel({ dsId, columns, filters }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/datasources/${dsId}/table`, {
        filters, search: debounced, sort_by: sortBy, sort_dir: sortDir, page, page_size: PAGE_SIZE,
      });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  }, [dsId, filters, debounced, sortBy, sortDir, page]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(0); }, [filters]);

  const cols = columns.map((c) => c.name);
  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("asc"); }
  };
  const pages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="rounded-2xl bg-card shadow-sm ring-1 ring-black/[0.03] overflow-hidden" data-testid="data-table-panel">
      <div className="flex items-center justify-between gap-4 p-4 border-b border-border">
        <h3 className="font-display font-bold text-base">Data Mentah</h3>
        <div className="relative w-64 max-w-full">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari..."
            className="pl-9 h-8 text-sm" data-testid="table-search" />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-secondary/80 backdrop-blur-sm z-[1]">
            <tr>
              {cols.map((c) => (
                <th key={c} onClick={() => toggleSort(c)}
                  className="text-left px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap cursor-pointer hover:text-foreground select-none"
                  data-testid={`th-${c}`}>
                  <span className="inline-flex items-center gap-1">
                    {c}
                    {sortBy === c && (sortDir === "asc" ? <CaretUp size={11} /> : <CaretDown size={11} />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={cols.length} className="px-3 py-10 text-center text-xs text-muted-foreground animate-pulse">Memuat…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={cols.length} className="px-3 py-10 text-center text-xs text-muted-foreground">Tidak ada data</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="border-t border-border hover:bg-secondary/50 transition-colors">
                {cols.map((c) => (
                  <td key={c} className="px-3 py-2 whitespace-nowrap max-w-[240px] truncate text-xs tabular" title={String(r[c] ?? "")}>
                    {String(r[c] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between p-3 border-t border-border text-xs text-muted-foreground">
        <span className="tabular">{total.toLocaleString("id-ID")} baris</span>
        <div className="flex items-center gap-3">
          <span className="tabular">Hal {pages ? page + 1 : 0} / {pages}</span>
          <div className="flex gap-1">
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}
              className="p-1.5 rounded-lg ring-1 ring-border disabled:opacity-30 hover:bg-secondary" data-testid="table-prev">
              <CaretLeft size={14} />
            </button>
            <button disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}
              className="p-1.5 rounded-lg ring-1 ring-border disabled:opacity-30 hover:bg-secondary" data-testid="table-next">
              <CaretRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
